// Template4.tsx - Updated with Photo Synchronization Fixes
"use client";

import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Sparkles,
  Star,
  Crop,
  X,
  Check,
  Loader2,
  Trash2,
  Building2,
  IdCard
} from "lucide-react";

import useTemplateBase, {
  Property as BaseProperty,
  PropertyList as BasePropertyList,
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import openHouseBadge from "../../assets/star_red.png";

interface PropertyList extends BasePropertyList { }

interface Property extends BaseProperty {
  propertyListType?: BaseProperty["propertyListType"] & {
    custom_template_data?: Record<string, any>;
  };
  is_open_house?: boolean;
}

interface Template4FlyerData {
  price: number | null;
  address: string;
  state?: string;
  city?: string;
  bedrooms: number | null;
  bathrooms: number | null;
  area: number | null;
  sqft: number | null;
  description: string;
  text_section: string;
  headline: string;
  features: { id?: number; feature_name: string }[];
  heroImage: string;
  secondaryImage?: string;
  photo2?: string;
  photo3?: string;
  photo4?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  list_name?: string;
  photos?: string[];
}

interface Template4Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: any[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updated: Partial<Template4FlyerData>) => void;
  onPropertyListChange?: (list: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  readOnly?: boolean;
}

// ✅ Custom tag color constants
const TAG_COLOR = "#9e7c39d4";
const TAG_BG = "#faecd1bf";
const TAG_BORDER = "#875f10d4";
const RESOLUTION_HINT =
  "For better experience, please upload the template image to ensure proper resolution, height, and width.";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

const Template4: React.FC<Template4Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
  readOnly = false,
}) => {
  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  // ✅ Photo refs for all photos (same as Template7)
  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);
  const photo4Ref = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // Track target box pixel resolutions for hints (measured from the container)
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const secondaryContainerRef = useRef<HTMLDivElement | null>(null);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [boxSecondaryRes, setBoxSecondaryRes] = useState<string | null>(null);

  // Custom list creation state
  const [isCustomList, setIsCustomList] = useState<boolean>(false);
  const [customListName, setCustomListName] = useState<string>("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);

  // Custom fields editor state
  const [editingCustom4, setEditingCustom4] = useState(false);
  const [customFields4, setCustomFields4] = useState<
    { key: string; value: string }[]
  >([]);

  // ✅ USE TEMPLATE BASE HOOK - Now includes updatePhotoAtIndex and removePhotoAtIndex
  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    updatePropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePhotoAtIndex, // ✅ ADD: From useTemplateBase
    removePhotoAtIndex, // ✅ ADD: From useTemplateBase
    uploadCroppedPhoto,
    toast,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  // Sync local property lists when prop changes
  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  // Reset local override when property changes
  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  // Initialize custom fields from property
  useEffect(() => {
    const existingCustomData =
      property?.propertyListType?.custom_template_data?.template4 || [];
    setCustomFields4(
      Array.isArray(existingCustomData) ? existingCustomData : []
    );
  }, [property?.propertyListType?.custom_template_data?.template4]);

  // Measure container pixel dimensions and update required box resolutions
  useEffect(() => {
    const measure = () => {
      try {
        if (heroContainerRef.current) {
          const w = Math.round(heroContainerRef.current.clientWidth);
          const h = Math.round(heroContainerRef.current.clientHeight);
          setBoxHeroRes(`${w}×${h}`);
        }
        if (secondaryContainerRef.current) {
          const w2 = Math.round(secondaryContainerRef.current.clientWidth);
          const h2 = Math.round(secondaryContainerRef.current.clientHeight);
          setBoxSecondaryRes(`${w2}×${h2}`);
        }
      } catch (err) {
        // ignore
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    // Use ResizeObserver when available for live updates
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (heroContainerRef.current) ro.observe(heroContainerRef.current);
      if (secondaryContainerRef.current) ro.observe(secondaryContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // Determine currently selected list - prioritize local override
  const selectedList =
    localSelectedListOverride ??
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "";

  const resizeTextareaToContent = (
    textarea: HTMLTextAreaElement | null
  ) => {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    resizeTextareaToContent(descriptionRef.current);
  }, [flyerData.description]);

  // Combine prop lists with local lists, provide defaults if empty
  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Just Listed" },
        { id: 2, list_name: "Priced to Sell" },
        { id: 3, list_name: "Open House" },
        { id: 4, list_name: "Seller Motivated" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "Just Listed";

  // Custom fields editor functions
  const addCustomField4 = () =>
    setCustomFields4((p) => [...p, { key: "", value: "" }]);

  const updateCustomField4 = (i: number, field: "key" | "value", v: string) => {
    setCustomFields4((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };

  const removeCustomField4 = (i: number) =>
    setCustomFields4((p) => p.filter((_, idx) => idx !== i));

  const saveCustomForTemplate4 = async () => {
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData(
          "template4",
          customFields4.filter((f) => f.key || f.value)
        );
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template4: customFields4,
              },
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
        }
      }
      setEditingCustom4(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  // === OPEN HOUSE LOGIC ===
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (!property?.id || selectedList !== "Open House") return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "This flyer will now show Open House styling"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save Open House status",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  // === SAVE CUSTOM LIST ===
  const saveCustomList = async () => {
    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);

      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      setIsCustomList(false);
      setCustomListName("");

      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and selected`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // ✅ FIXED: Hero Image Upload Handler (same as Template7)
  const handleHeroFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input for re-selection
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      // Show preview immediately
      // Validate uploaded image resolution against the container box resolution (if available)
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxHeroRes) {
          const parts = boxHeroRes.split("×").map((s) => Number(s));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            if (w !== rw || h !== rh) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${w}×${h}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
                variant: "default",
              });
            }
          }
        }

        // Show preview immediately after validation
        setFlyerData((prev) => ({ ...prev, heroImage: dataUrl }));
        // This freshly-picked photo becomes the new "original" for this
        // session immediately, so Edit Image always has an untouched source
        // to re-crop from, right from this point on. Mirrors Template1.tsx.
        setOriginalHeroImage(dataUrl);
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);
        // Persist this same freshly-picked photo as the untouched "original"
        // separately from the displayed image, so Edit Image can always
        // re-crop from the full source later instead of re-cropping an
        // already-cropped result. Piggybacks on the PATCH already being sent
        // here (Template4 uploads the hero image immediately, unlike
        // Template1's deferred flow) so there's no extra request.
        formData.append("original_image", file);

        onDirtyChange?.(true);
        onChange?.({ heroImage: dataUrl });

        // Upload to server
        await axiosInstance.patch(
          `${endpoints.properties}${property?.id}/`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast({ title: "Success", description: "Main photo updated" });
        onDirtyChange?.(false);
        // natural size will be captured in onLoad handler when image renders
      } catch (err) {
        console.error("Failed to upload hero image:", err);
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 320;
  const EDIT_CROP_H = 320; // matches the square hero box aspect ratio

  const openEditImage = async () => {
    if (!flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  // ─── Edit Image for secondary/gallery photos (same non-destructive pattern
  // as the hero photo above, reusing the shared updatePhotoAtIndex/
  // uploadCroppedPhoto helpers from TemplateBase so original-capture stays
  // centralized) ──
  const SECONDARY_CROP_W = 320;
  const SECONDARY_CROP_H = 320; // matches the square secondary-photo box aspect ratio

  const [secondaryCropModal, setSecondaryCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    field: "photo2" | "photo3" | "photo4" | null;
  }>({ open: false, imageUrl: "", field: null });

  const openEditSecondaryPhoto = async (
    field: "photo2" | "photo3" | "photo4",
    currentSrc: string
  ) => {
    if (!currentSrc) return;

    // Keyed by slot name ("photo2"/"photo3"/"photo4"), not array position —
    // see TemplateBase.tsx's updatePhotoAtIndex for why.
    let originalSource = (flyerData.original_photos && flyerData.original_photos[field]) || "";

    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_photos?.[field] || "";
      } catch (err) {
        console.error("Failed to check for existing original photo:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal by re-saving the current photo
      // through updatePhotoAtIndex (captureOriginal defaults to true), so
      // this and every future edit has a stable, full source to re-crop
      // from instead of the current display.
      originalSource = currentSrc;
      if (!currentSrc.startsWith("data:")) {
        updatePhotoAtIndex(currentSrc, field).catch((err) => {
          console.error("Failed to seed original photo:", err);
        });
      }
    }

    setSecondaryCropModal({
      open: true,
      imageUrl: getEditableImageUrl(originalSource),
      field,
    });
  };

  const handleSecondaryEditConfirm = async (croppedDataUrl: string) => {
    const field = secondaryCropModal.field;
    if (!field) return;
    try {
      const newUrl = await uploadCroppedPhoto(croppedDataUrl);
      await updatePhotoAtIndex(newUrl, field, { captureOriginal: false });
      toast({ title: "Success", description: "Photo updated" });
    } catch (err) {
      console.error("Failed to save edited photo:", err);
      toast({
        title: "Error",
        description: "Failed to save edited photo",
        variant: "destructive",
      });
    } finally {
      setSecondaryCropModal({ open: false, imageUrl: "", field: null });
    }
  };

  const handleSecondaryEditCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "", field: null });
  };

  // ✅ FIXED: Secondary Photo Handler (same as Template7)
  const handleSecondaryFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "photo2" | "photo3" | "photo4"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input value for re-selection
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Max 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      // Show preview immediately - update both secondaryImage and the specific field
      // Validate uploaded image resolution against the container box resolution (if available)
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxSecondaryRes) {
          const parts = boxSecondaryRes.split("×").map((s) => Number(s));
          if (parts.length === 2) {
            const [rw, rh] = parts;
            if (w !== rw || h !== rh) {
              toast({
                title: "Resolution Suggestion",
                description: `Uploaded image is ${w}×${h}. Recommended: ${boxSecondaryRes}. If possible, upload this resolution for best results.`,
                variant: "default",
              });
            }
          }
        }

        // Show preview immediately after validation - update both secondaryImage and the specific field
        setFlyerData((prev) => ({
          ...prev,
          [field]: dataUrl,
          ...(field === "photo2" ? { secondaryImage: dataUrl } : {}),
        }));
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        // Upload to server
        const res = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });

        if (res?.data?.url) {
          // Update with server URL using updatePhotoAtIndex
          const imageUrl = res.data.url;

          // Also update secondaryImage if it's photo2
          if (field === "photo2") {
            setFlyerData((prev) => ({
              ...prev,
              secondaryImage: imageUrl,
            }));
          }

          updatePhotoAtIndex(imageUrl, field);
          toast({ title: "Success", description: "Photo uploaded", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload photo:", err);
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ✅ ADD: Remove Hero Photo Handler
  const handleRemoveHeroPhoto = () => {
    setFlyerData((prev) => ({ ...prev, heroImage: "" }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: "" });
    toast({ title: "Removed", description: "Main photo removed" });
  };

  // ✅ ADD: Remove Secondary Photo Handler (same as Template7)
  const handleRemoveSecondaryPhoto = (
    field: "photo2" | "photo3" | "photo4"
  ) => {
    // Also clear secondaryImage if removing photo2
    if (field === "photo2") {
      setFlyerData((prev) => ({
        ...prev,
        secondaryImage: "",
      }));
    }
    removePhotoAtIndex(field);
    toast({ title: "Removed", description: "Photo removed" });
  };

  // ✅ ADD: Helper function to get photo from multiple sources (same as Template7)
  const getPhotoSrc = (
    field: "photo2" | "photo3" | "photo4",
    index: number
  ): string => {
    // 1. Check flyerData field directly (e.g., flyerData.photo2)
    const fieldValue = flyerData[field];
    if (
      fieldValue &&
      typeof fieldValue === "string" &&
      fieldValue.trim() !== ""
    ) {
      return fieldValue;
    }

    // 2. Check flyerData.photos array
    if (Array.isArray(flyerData.photos) && flyerData.photos[index]) {
      const photoFromArray = flyerData.photos[index];
      if (
        photoFromArray &&
        typeof photoFromArray === "string" &&
        photoFromArray.trim() !== ""
      ) {
        return photoFromArray;
      }
    }

    // 3. Check property.photos array as fallback
    if (property && Array.isArray(property.photos) && property.photos[index]) {
      const propertyPhoto = property.photos[index];
      if (
        propertyPhoto &&
        typeof propertyPhoto === "string" &&
        propertyPhoto.trim() !== ""
      ) {
        return propertyPhoto;
      }
    }

    return "";
  };

  // ✅ For Template4, also handle secondaryImage which maps to photo2 (index 0)
  const getSecondaryImageSrc = (): string => {
    // Check secondaryImage first
    if (
      flyerData.secondaryImage &&
      typeof flyerData.secondaryImage === "string" &&
      flyerData.secondaryImage.trim() !== ""
    ) {
      return flyerData.secondaryImage;
    }

    // Fall back to photo2/photos[0]
    return getPhotoSrc("photo2", 0);
  };

  return (
    <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch', transform: 'rotateX(180deg)' }}>
      <div className="bg-slate-900 rounded-3xl shadow-none text-white font-arial relative overflow-hidden" style={{ minWidth: '828px', width: '100%', transform: 'rotateX(180deg)', border: 'none', boxShadow: 'none' }}>
        {editingCustom4 && (
          <div className="absolute right-6 top-20 z-50 bg-slate-800 p-4 rounded-lg shadow-xl w-80 border border-slate-600">
            <div className="flex items-center justify-between mb-3">
              <strong className="text-amber-400">Custom Fields</strong>
              <div className="flex gap-2">
                <button
                  onClick={addCustomField4}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  Add
                </button>
                <button
                  onClick={() => setEditingCustom4(false)}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {customFields4.map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <input
                    value={f.key}
                    onChange={(e) =>
                      updateCustomField4(idx, "key", e.target.value)
                    }
                    placeholder="Key"
                    className="bg-slate-700 border border-slate-600 px-2 py-1 flex-1 text-sm rounded text-white placeholder-slate-400"
                  />
                  <input
                    value={f.value}
                    onChange={(e) =>
                      updateCustomField4(idx, "value", e.target.value)
                    }
                    placeholder="Value"
                    className="bg-slate-700 border border-slate-600 px-2 py-1 flex-1 text-sm rounded text-white placeholder-slate-400"
                  />
                  <button
                    onClick={() => removeCustomField4(idx)}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={saveCustomForTemplate4}
                className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* ✅ FIXED: HERO SECTION - TWO PHOTOS with proper sync */}
        <div className="relative bg-slate-900 rounded-t-3xl overflow-hidden">
          {/* Photos Grid */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              padding: '24px',
              boxSizing: 'border-box',
              width: '100%',
              alignItems: 'stretch',
            }}
          >

            {/* Main Photo (Hero Image) */}
            <div ref={heroContainerRef} className="relative rounded-2xl overflow-hidden bg-slate-800/60 border-2 border-slate-700 group" style={{
              flex: '1 1 0',
              height: '380px',
              maxHeight: '380px',
              minHeight: '380px',
              minWidth: 0,
            }}>
              {/* Resolution Hint - only when NO image and in edit mode */}
              {!readOnly && (
                <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    {boxHeroRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxHeroRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}

              {isOpenHouseConfirmed && (
                <div className="absolute top-0 right-0  md:top-4 md:right-4 z-20" style={{ marginLeft: '-10px', marginTop: '-5px', paddingLeft: '10px', marginRight: '-3px', paddingRight: '10px' }}>
                  <div className="relative w-28 md:w-34">
                    <img src={openHouseBadge} alt="Open House" className="w-full drop-shadow-xl" />
                    <span className="absolute inset-0 flex items-center justify-center text-white font-extrabold text-xs md:text-sm tracking-wide text-center pointer-events-none">
                      OPEN<br />HOUSE
                    </span>
                  </div>
                </div>
              )}

              {flyerData.heroImage ? (
                <>
                  <div className="absolute inset-0 w-full h-full">
                    <img
                      src={flyerData.heroImage}
                      alt="Main property"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                      className="transition-transform group-hover:scale-105 duration-700"
                      onError={(e) => {
                        console.error("Failed to load hero image:", flyerData.heroImage);
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                      <Button
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Change
                      </Button>
                      <Button
                        size="sm"
                        onClick={openEditImage}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Crop className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <label
                  htmlFor="hero-upload-t4"
                  className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 hover:text-amber-400 transition-all duration-300 cursor-pointer"
                >
                  <ImageIcon className="h-16 w-16 md:h-20 md:w-20 mb-4" />
                  <p className="text-xl md:text-2xl font-bold">Upload Main Photo</p>
                  <p className="text-sm mt-2 text-slate-500">Click to upload</p>
                </label>
              )}
            </div>

            {/* Secondary Photo */}
            <div ref={secondaryContainerRef} className="relative rounded-2xl overflow-hidden bg-slate-800/60 border-2 border-slate-700 group" style={{
              flex: '1 1 0',
              height: '380px',
              maxHeight: '380px',
              minHeight: '380px',
              minWidth: 0,
            }}>
              {/* Resolution Hint - only when NO image and in edit mode */}
              {!readOnly && (
                <div className="absolute bottom-3 left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-black/70 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    {boxSecondaryRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxSecondaryRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}

              {(() => {
                const secondarySrc = getSecondaryImageSrc();
                return secondarySrc ? (
                  <>
                    <img
                      src={secondarySrc}
                      alt="Secondary view"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                      className="transition-transform group-hover:scale-105 duration-700"
                      onError={(e) => {
                        console.error("Failed to load secondary image:", secondarySrc);
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    {!readOnly && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => photo2Ref.current?.click()}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Change
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => openEditSecondaryPhoto("photo2", secondarySrc)}
                          className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                        >
                          <Crop className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <label
                    htmlFor="secondary-upload-t4"
                    className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 hover:text-amber-400 transition-all duration-300 cursor-pointer"
                  >
                    <ImageIcon className="h-16 w-16 md:h-20 md:w-20 mb-4" />
                    <p className="text-xl md:text-2xl font-bold">Upload Second Photo</p>
                  </label>
                );
              })()}
            </div>
          </div>

          {/* List Selector & Open House Badge */}
          <div className="absolute top-6 md:top-8 left-6 md:left-8 z-10">
            <div className="flex flex-col gap-2">
              {readOnly ? (
                <div
                  className="max-w-[240px] w-auto backdrop-blur-md font-bold rounded-xl px-4 py-2 flex items-center gap-2 mt-5 ml-3 whitespace-nowrap"
                  style={{
                    background: TAG_BG,
                    color: TAG_COLOR,
                    border: `1px solid ${TAG_BORDER}`,
                  }}
                >
                  <img
                    src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/star_20.png"
                    alt="Tag Icon"
                    className="h-6 w-6 flex-shrink-0 object-contain"
                  />
                  <span>
                    {listSelectValue || "Just Listed"}
                  </span>
                </div>
              ) : (
                <>
                  {isCustomList ? (
                    <div
                      className="flex items-center gap-2 backdrop-blur-md rounded-xl px-4 py-2"
                      style={{
                        background: TAG_BG,
                        border: `1px solid ${TAG_BORDER}`,
                      }}
                    >
                      <input
                        type="text"
                        value={customListName}
                        onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                        placeholder="Custom tag (max 20 chars)"
                        maxLength={20}
                        className="bg-transparent px-2 py-1 text-sm w-32 md:w-40 outline-none border-none"
                        style={{ color: TAG_COLOR }}
                        autoFocus
                        disabled={isSavingCustomList}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isSavingCustomList) {
                            e.preventDefault();
                            saveCustomList();
                          }
                          if (e.key === "Escape") cancelCustomList();
                        }}
                      />
                      <button
                        onClick={saveCustomList}
                        disabled={!customListName.trim() || isSavingCustomList}
                        className="p-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ background: "rgba(135,95,16,0.15)" }}
                        title="Save custom tag"
                      >
                        {isSavingCustomList ? (
                          <Loader2 className="h-4 w-4 animate-spin" style={{ color: TAG_COLOR }} />
                        ) : (
                          <Check className="h-4 w-4" style={{ color: TAG_COLOR }} />
                        )}
                      </button>
                      <button
                        onClick={cancelCustomList}
                        disabled={isSavingCustomList}
                        className="p-1.5 rounded disabled:opacity-50 transition-colors"
                        style={{ background: "rgba(135,95,16,0.15)" }}
                        title="Cancel"
                      >
                        <X className="h-4 w-4" style={{ color: TAG_COLOR }} />
                      </button>
                    </div>
                  ) : (
                    <Select value={listSelectValue} onValueChange={handleListSelection}>
                      <SelectTrigger
                        className="w-auto min-w-[140px] max-w-[260px] backdrop-blur-md font-bold rounded-xl hover:opacity-90 focus:ring-2 transition-all"
                        style={{
                          background: TAG_BG,
                          color: TAG_COLOR,
                          borderColor: TAG_BORDER,
                          // @ts-ignore
                          "--tw-ring-color": TAG_BORDER,
                        }}
                      >
                        <SelectValue placeholder="-- Select List --" />
                      </SelectTrigger>
                      <SelectContent
                        className="
    bg-white
    border
    border-gray-300
    shadow-xl
   
    text-black
  "
                      >
                        {allLists.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No lists available
                          </SelectItem>
                        ) : (
                          allLists
                            .filter((list) => list.list_name !== "Open House")
                            .map((list) => (
                              <SelectItem
                                key={list.id}
                                value={list.list_name}
                                className="
            cursor-pointer
           
           
          
          "
                              >
                                <div className="flex items-center gap-2">
                                  <Sparkles className="h-4 w-4 " />
                                  {list.list_name}
                                </div>
                              </SelectItem>
                            ))
                        )}

                        <SelectItem
                          value="__custom__"
                          className="
      cursor-pointer
      text-black
      focus:bg-black
      focus:text-white
      data-[highlighted]:bg-black
      data-[highlighted]:text-white
    "
                        >
                          <div className="flex items-center gap-2">
                            {/* <Sparkles className="h-4 w-4 text-black" /> */}
                            + Add Custom Tag
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Hidden File Inputs */}
          <input
            id="hero-upload-t4"
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleHeroFileChange}
          />
          <input
            id="secondary-upload-t4"
            ref={photo2Ref}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleSecondaryFileChange(e, "photo2")}
          />
          {!readOnly && (
            <ImageCropModal
              open={cropModal.open}
              imageUrl={cropModal.imageUrl}
              title="Adjust Property Photo"
              outputSize={700}
              cropW={EDIT_CROP_W}
              cropH={EDIT_CROP_H}
              onConfirm={handleEditImageConfirm}
              onCancel={handleEditImageCancel}
            />
          )}
          {!readOnly && (
            <ImageCropModal
              open={secondaryCropModal.open}
              imageUrl={secondaryCropModal.imageUrl}
              title="Adjust Property Photo"
              outputSize={700}
              cropW={SECONDARY_CROP_W}
              cropH={SECONDARY_CROP_H}
              onConfirm={handleSecondaryEditConfirm}
              onCancel={handleSecondaryEditCancel}
            />
          )}

          {/* Custom fields (read-only display) */}
          {property?.propertyListType?.custom_template_data?.template4 &&
            Array.isArray(property.propertyListType.custom_template_data.template4) &&
            property.propertyListType.custom_template_data.template4.length > 0 && (
              <div className="px-6 md:px-10 py-4">
                <div className="bg-white/5 rounded-lg p-4 border border-slate-700 text-white">
                  <h4 className="font-semibold text-sm text-amber-300 mb-2">Custom Fields</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-white/90">
                    {property.propertyListType.custom_template_data.template4.map(
                      (f: any, i: number) => (
                        <div key={i} className="flex justify-between">
                          <span className="font-medium">{f.key}</span>
                          <span>{f.value}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
        {/* Price & Address Section */}
        <div className="px-6 md:px-10 py-8 md:py-10 bg-gradient-to-b from-slate-900 to-slate-800">
          {readOnly ? (
            <p className="text-4xl md:text-5xl lg:text-6xl font-black text-white w-full mb-4 md:mb-6">
              {flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : "$0"}
            </p>
          ) : (
            <input
              type="text"
              value={flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : ""}
              onChange={(e) => {
                const num = e.target.value.replace(/[^0-9]/g, "");
                handleInputChange("price", num ? Number(num) : null);
              }}
              placeholder="$0"
              className="text-4xl md:text-5xl lg:text-6xl font-black bg-transparent border-none outline-none text-white placeholder-white/30 w-full mb-4 md:mb-6"
            />
          )}

          <div className="flex items-start gap-3 md:gap-4">
            <MapPin className="h-7 w-7 md:h-9 md:w-9 text-amber-400 flex-shrink-0 mt-1" />
            {readOnly ? (
              <p className="text-lg md:text-xl lg:text-2xl text-white/90 w-full">
                {flyerData.address || ""}
              </p>
            ) : (
              <textarea
                rows={2}
                value={flyerData.address || ""}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="123 Luxury Lane, Beverly Hills, CA 90210"
                className="text-lg md:text-xl lg:text-2xl bg-transparent resize-none border-none outline-none text-white/90 placeholder-white/40 w-full"
              />
            )}
          </div>
        </div>

        {/* CONTENT SECTION */}
        <div className="px-6 md:px-10 py-8 md:py-10 space-y-10 md:space-y-12">
          {/* Headline */}
          {readOnly ? (
            <p className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 w-full">
              {flyerData.headline || ""}
            </p>
          ) : (
            <textarea
              value={flyerData.headline || ""}
              onChange={(e) => handleInputChange("headline", e.target.value)}
              placeholder="Luxury Living Redefined – Your Dream Home Awaits"
              className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 bg-transparent border-none outline-none w-full placeholder-amber-400/40"
            />
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: Bed, label: "Bedrooms", field: "bedrooms" as const },
              { icon: Bath, label: "Bathrooms", field: "bathrooms" as const },
              { icon: Square, label: "Sq Ft", field: "area" as const },
            ].map(({ icon: Icon, label, field }) => (
              <div
                key={field}
                className="bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 md:p-8 text-center hover:bg-slate-800/80 hover:border-slate-600 transition-all duration-300"
              >
                <Icon className="h-12 w-12 md:h-14 md:w-14 text-amber-400 mx-auto mb-4" />
                {readOnly ? (
                  <p className="text-3xl md:text-4xl font-black text-white w-full text-center">
                    {field === "area"
                      ? flyerData.area ?? flyerData.sqft ?? 0
                      : flyerData[field] ?? 0}
                  </p>
                ) : (
                  <input
                    type="number"
                    min="0"
                    value={field === "area"
                      ? flyerData.area ?? flyerData.sqft ?? ""
                      : flyerData[field] ?? ""}
                    onChange={(e) => handleInputChange(field, e.target.value ? Number(e.target.value) : null)}
                    className="text-3xl md:text-4xl font-black bg-transparent border-none outline-none text-white w-full text-center [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                )}
                <p className="text-slate-400 mt-3 text-base md:text-lg font-medium">
                  {label}
                </p>
              </div>
            ))}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-amber-400 font-bold text-xl md:text-2xl mb-6 flex items-center">
              <Star className="h-6 w-6 md:h-8 md:w-8 mr-3 flex-shrink-0" />
              About This Masterpiece
            </h3>
            {readOnly ? (
              <p className="w-full text-slate-300 text-base md:text-lg leading-relaxed whitespace-pre-wrap">
                {flyerData.description || ""}
              </p>
            ) : (
              <textarea
                ref={descriptionRef}
                value={flyerData.description || ""}
                rows={1}
                onChange={(e) => { handleInputChange("description", e.target.value); resizeTextareaToContent(e.currentTarget); }}
                placeholder="Step into luxury with this breathtaking residence..."
                className="w-full bg-transparent text-slate-300 text-base md:text-lg leading-relaxed resize-none overflow-hidden border-none outline-none placeholder-slate-500"
              />
            )}
          </div>

          {/* Features */}
          <div>
            <h3 className="text-amber-400 font-bold text-xl md:text-2xl mb-5">
              Key Features & Amenities
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {flyerData.features.map((feat: any, i: number) => (
                <div
                  key={feat.id ?? i}
                  className="
          flex items-center
          w-full
          bg-slate-800/50
          border border-slate-700
          rounded-2xl
          px-5 py-4
          hover:bg-slate-800/70
          hover:border-slate-600
          transition-all
          group
        "
                >
                  {/* Bullet */}
                  <div className="w-4 h-4 bg-amber-400 rounded-full mr-4 flex-shrink-0" />

                  {/* Feature Text */}
                  {readOnly ? (
                    <span className="flex-1 text-white text-lg leading-tight">
                      {feat.feature_name}
                    </span>
                  ) : (
                    <textarea
                      value={feat.feature_name}
                      onChange={(e) => handleFeatureChange(i, e.target.value)}
                      onBlur={() => handleFeatureBlur(i)}
                      maxLength={30}
                      rows={1}
                      className="flex-1 bg-transparent border-none outline-none text-white text-lg leading-tight resize-none overflow-hidden whitespace-pre-wrap break-words p-0 m-0 min-h-0 h-auto"
                      style={{ height: "28px" }}
                      placeholder="Enter feature..."
                    />
                  )}

                  {/* Delete Button */}
                  {!readOnly && (
                    <button
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleFeatureDelete(i);
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        handleFeatureDelete(i);
                      }}
                      className="
              ml-3
              text-red-400
              hover:text-red-300
              text-2xl
              opacity-0
              group-hover:opacity-100
              transition-opacity
              flex-shrink-0
              leading-none
            "
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add Feature Button */}
            {!readOnly && flyerData.features.length < 10 && (
              <Button
                onClick={addFeature}
                variant="outline"
                className="
        mt-4
        h-[64px]
        w-full
        border-2 border-dashed
        border-amber-600
        text-amber-400
        bg-amber-400/10
        hover:border-amber-400
        hover:text-amber-400
        hover:bg-amber-600/10
        rounded-xl
        text-lg
        font-medium
        transition-all
      "
              >
                + Add Feature
              </Button>
            )}
          </div>
          {/* Agent Card */}
          <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border border-amber-400/30 rounded-2xl p-3 md:p-3 overflow-hidden">


            <div className="flex flex-row items-center gap-5 md:gap-6">

              {/* Agent Avatar - LEFT */}
              <div className="w-[150px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                {agentData?.avatar ? (
                  <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="h-8 w-8 text-slate-500" />
                )}
              </div>

              {/* Agent Info - CENTER */}
              <div className="flex-1 min-w-0 flex flex-col justify-center items-center rounded-xl px-4 py-3 md:px-6 md:py-5 text-center">

                {/* Agent Name */}
                <p className="text-2xl md:text-3xl font-bold text-white mb-3 truncate w-full">
                  {flyerData.agentName || ""}
                </p>

                <div className="space-y-3 text-slate-300 text-sm md:text-base w-full flex flex-col items-center justify-center">

                  {/* Phone */}
                  <div className="inline-flex items-center gap-1.5 mx-auto">
                    <Phone className="h-5 w-5 md:h-6 md:w-6 text-amber-400 flex-shrink-0" />
                    <span className="font-medium text-slate-300">{flyerData.agentPhone || ""}</span>
                  </div>

                  {/* Email */}
                  <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                    <Mail className="h-5 w-5 md:h-6 md:w-6 text-amber-400 underline  flex-shrink-0" />
                    <span className="font-medium text-slate-300 underline">
                      {/* {flyerData.agentEmail || ""} */}
                       Click here to email Me
                      </span>
                    
                  </div>

                  {/* Company */}
                  <div className="inline-flex items-center gap-1.5 mx-auto">
                    <Building2 className="h-5 w-5 md:h-6 md:w-6 text-amber-400 flex-shrink-0" />
                    <span className="font-medium text-slate-300">{flyerData.companyName || agentData?.company || ""}</span>
                  </div>

                  {/* License Number */}
                  {agentData?.license_number && (
                    <div className="inline-flex items-center gap-1.5 mx-auto pl-5">
                      <IdCard className="h-5 w-5 md:h-6 md:w-6 text-amber-400 flex-shrink-0" />
                      <span className="font-medium text-slate-300">
                        License #: {agentData.license_number}
                      </span>
                    </div>
                  )}

                </div>
              </div>
              {/* Agent Info closes */}

              {/* Company Logo - RIGHT */}
              <div className="w-[180px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">     {agentData?.company_logo ? (
                <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover rounded-xl" />
              ) : (
                <span className="text-xs text-slate-400 text-center px-1">Company Logo</span>
              )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template4;
